import React, { useState, useEffect } from 'react';
import { Plus, Play, Pause, BarChart2, Mail, Phone, Globe, MessageSquare, Loader2, Send, CheckCircle2, Clock, Ban, Users, TrendingUp, Zap, FileText, RefreshCw, MessageCircle } from 'lucide-react';
import api from '../services/api';
import { formatDate } from '../utils/dateUtils';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardAction,
  CardFooter,
} from '../components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from '../components/ui/use-toast';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Campaign {
  id: number;
  title: string;
  description?: string;
  campaign_type: 'one_off' | 'ongoing';
  status: 'draft' | 'active' | 'paused' | 'completed' | 'cancelled';
  trigger_type: 'immediate' | 'scheduled' | 'event_based';
  scheduled_at?: string;
  audience_type: string;
  message_content: string;
  message_type: string;
  enabled_channels: string[];
  total_recipients: number;
  sent_count: number;
  delivered_count: number;
  opened_count: number;
  clicked_count: number;
  replied_count: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

interface CampaignTemplate {
  id: number;
  name: string;
  description?: string;
  category: string;
  subject?: string;
  message_content: string;
  message_type: string;
  variables: string[];
  usage_count: number;
  created_at: string;
}

// Phase 4: WhatsApp Template Integration (2026-01-19)
interface WhatsAppTemplate {
  id: number;
  name: string;
  category: string;
  language_code: string;
  components: any[];
  variables: string[];
  approval_status: string;
  quality_score?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const STATUS_BADGE: Record<string, string> = {
  active:    'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  paused:    'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300',
  completed: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300',
  cancelled: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300',
  draft:     'border-border bg-muted/40 text-muted-foreground',
};

const STATUS_LEFT: Record<string, string> = {
  active:    'bg-emerald-500',
  paused:    'bg-amber-500',
  completed: 'bg-blue-500',
  cancelled: 'bg-red-500',
  draft:     'bg-muted-foreground/30',
};

const WA_STATUS_BADGE: Record<string, string> = {
  approved: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  rejected: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300',
  pending:  'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300',
};

const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case 'active':    return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />;
    case 'paused':    return <Pause className="h-3.5 w-3.5 text-amber-500" />;
    case 'completed': return <CheckCircle2 className="h-3.5 w-3.5 text-blue-500" />;
    case 'cancelled': return <Ban className="h-3.5 w-3.5 text-red-500" />;
    default:          return <Clock className="h-3.5 w-3.5 text-muted-foreground" />;
  }
};

const ChannelIcon: React.FC<{ channel: string; size?: string }> = ({ channel, size = 'h-3.5 w-3.5' }) => {
  switch (channel) {
    case 'email':    return <Mail className={size} />;
    case 'whatsapp': return <Phone className={size} />;
    case 'website':  return <Globe className={size} />;
    default:         return <MessageSquare className={size} />;
  }
};

const calcEngagement = (c: Campaign) => {
  if (c.delivered_count === 0) return 0;
  return Math.round(((c.opened_count + c.clicked_count + c.replied_count) / c.delivered_count) * 100);
};

// ─── Form defaults ────────────────────────────────────────────────────────────
interface CampaignFormData {
  title: string;
  description: string;
  campaign_type: 'one_off' | 'ongoing';
  trigger_type: 'immediate' | 'scheduled' | 'event_based';
  scheduled_at: string;
  audience_type: string;
  audience_filters: Record<string, any>;
  message_content: string;
  message_type: string;
  media_url: string;
  enabled_channels: string[];
  send_only_business_hours: boolean;
  whatsapp_template_id: number | null;
  template_variables: Record<string, string>;
  rate_limit_per_minute: number;
  retry_failed: boolean;
  max_retries: number;
  enforce_24h_window: boolean;
  template_cooldown_hours: number;
  template_category: string;
}

const DEFAULT_FORM: CampaignFormData = {
  title: '',
  description: '',
  campaign_type: 'one_off',
  trigger_type: 'immediate',
  scheduled_at: '',
  audience_type: 'all',
  audience_filters: {},
  message_content: '',
  message_type: 'text',
  media_url: '',
  enabled_channels: ['website'],
  send_only_business_hours: false,
  whatsapp_template_id: null,
  template_variables: {},
  rate_limit_per_minute: 100,
  retry_failed: true,
  max_retries: 3,
  enforce_24h_window: true,
  template_cooldown_hours: 48,
  template_category: '',
};

const DEFAULT_TEMPLATE_FORM = {
  name: '',
  description: '',
  category: 'general',
  subject: '',
  message_content: '',
  message_type: 'text',
  variables: [] as string[],
};

const SELECT_CLS = "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

// ─── Page ─────────────────────────────────────────────────────────────────────
const CampaignsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [templates, setTemplates] = useState<CampaignTemplate[]>([]);
  const [whatsappTemplates, setWhatsappTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  const [formData, setFormData] = useState<CampaignFormData>({ ...DEFAULT_FORM });
  const [templateForm, setTemplateForm] = useState({ ...DEFAULT_TEMPLATE_FORM });

  // ── Data ────────────────────────────────────────────────────────────────────
  const fetchData = async () => {
    try {
      setLoading(true);
      const [campaignsRes, templatesRes] = await Promise.all([
        api.get('/campaigns/'),
        api.get('/campaigns/templates/'),
      ]);
      setCampaigns(campaignsRes.data.campaigns || []);
      setTemplates(templatesRes.data.templates || []);
      try {
        const waRes = await api.get('/campaigns/whatsapp-templates/');
        setWhatsappTemplates(waRes.data.templates || []);
      } catch {
        setWhatsappTemplates([]);
      }
    } catch {
      toast({ title: 'Failed to load campaigns', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleCreateCampaign = async () => {
    try {
      const payload = {
        ...formData,
        scheduled_at: formData.scheduled_at ? new Date(formData.scheduled_at).toISOString() : undefined,
      };
      await api.post('/campaigns/', payload);
      toast({ title: 'Campaign created successfully' });
      setCreateDialogOpen(false);
      setFormData({ ...DEFAULT_FORM });
      fetchData();
    } catch {
      toast({ title: 'Failed to create campaign', variant: 'destructive' });
    }
  };

  const handleCreateTemplate = async () => {
    try {
      await api.post('/campaigns/templates/', templateForm);
      toast({ title: 'Template created successfully' });
      setTemplateDialogOpen(false);
      setTemplateForm({ ...DEFAULT_TEMPLATE_FORM });
      fetchData();
    } catch {
      toast({ title: 'Failed to create template', variant: 'destructive' });
    }
  };

  const handleCampaignAction = async (campaignId: number, action: string) => {
    try {
      await api.post(`/campaigns/${campaignId}/${action}`);
      fetchData();
    } catch (err: any) {
      const message = err.response?.data?.detail || `Failed to ${action} campaign`;
      toast({ title: 'Action failed', description: message, variant: 'destructive' });
      fetchData();
    }
  };

  // ── Computed stats ───────────────────────────────────────────────────────────
  const totalDelivered  = campaigns.reduce((s, c) => s + c.delivered_count, 0);
  const totalOpened     = campaigns.reduce((s, c) => s + c.opened_count, 0);
  const totalReplied    = campaigns.reduce((s, c) => s + c.replied_count, 0);
  const activeCampaigns = campaigns.filter((c) => c.status === 'active').length;
  const overallEngagement = totalDelivered > 0
    ? Math.round(((totalOpened + totalReplied) / totalDelivered) * 100)
    : 0;

  // ── Render ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 py-4 px-4 md:py-6 lg:px-6">

      {/* ── Page Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 shrink-0">
            <Send className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Campaigns</h1>
            <p className="text-sm text-muted-foreground">
              Manage outreach campaigns across all channels
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={fetchData} className="gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setTemplateDialogOpen(true)} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" />
            New Template
          </Button>
          <Button size="sm" onClick={() => setCreateDialogOpen(true)} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" />
            New Campaign
          </Button>
        </div>
      </div>

      {/* ── KPI Strip ────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardDescription className="text-xs">Total Campaigns</CardDescription>
            <CardTitle className="text-2xl font-bold tabular-nums">{campaigns.length}</CardTitle>
            <CardAction>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                <Send className="h-4 w-4 text-primary" />
              </div>
            </CardAction>
          </CardHeader>
          <CardFooter className="px-4 pb-3 text-xs text-muted-foreground">
            <span className="text-emerald-600 font-medium">{activeCampaigns} active</span>
            <span className="mx-1">·</span>
            {campaigns.filter(c => c.status === 'completed').length} completed
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-emerald-500/5 to-card shadow-sm">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardDescription className="text-xs">Delivered</CardDescription>
            <CardTitle className="text-2xl font-bold tabular-nums">{totalDelivered.toLocaleString()}</CardTitle>
            <CardAction>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/10">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              </div>
            </CardAction>
          </CardHeader>
          <CardFooter className="px-4 pb-3 text-xs text-muted-foreground">
            Across all campaigns
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-amber-500/5 to-card shadow-sm">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardDescription className="text-xs">Opened</CardDescription>
            <CardTitle className="text-2xl font-bold tabular-nums">{totalOpened.toLocaleString()}</CardTitle>
            <CardAction>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500/10">
                <TrendingUp className="h-4 w-4 text-amber-500" />
              </div>
            </CardAction>
          </CardHeader>
          <CardFooter className="px-4 pb-3 text-xs text-muted-foreground">
            Total message opens
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-violet-500/5 to-card shadow-sm">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardDescription className="text-xs">Engagement Rate</CardDescription>
            <CardTitle className="text-2xl font-bold tabular-nums">{overallEngagement}%</CardTitle>
            <CardAction>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-500/10">
                <Zap className="h-4 w-4 text-violet-500" />
              </div>
            </CardAction>
          </CardHeader>
          <CardFooter className="px-4 pb-3 text-xs text-muted-foreground">
            {totalReplied.toLocaleString()} replies received
          </CardFooter>
        </Card>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────────── */}
      <Tabs defaultValue="campaigns" className="flex flex-col gap-4">
        <TabsList className="w-fit">
          <TabsTrigger value="campaigns" className="gap-1.5">
            <Send className="h-3.5 w-3.5" />
            Campaigns
            {campaigns.length > 0 && (
              <span className="ml-0.5 text-[10px] bg-primary/15 text-primary px-1.5 py-0.5 rounded-full font-medium">
                {campaigns.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-1.5">
            <FileText className="h-3.5 w-3.5" />
            Templates
            {templates.length > 0 && (
              <span className="ml-0.5 text-[10px] bg-primary/15 text-primary px-1.5 py-0.5 rounded-full font-medium">
                {templates.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="whatsapp" className="gap-1.5">
            <MessageCircle className="h-3.5 w-3.5" />
            WhatsApp
            {whatsappTemplates.length > 0 && (
              <span className="ml-0.5 text-[10px] bg-primary/15 text-primary px-1.5 py-0.5 rounded-full font-medium">
                {whatsappTemplates.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── Campaigns Tab ──────────────────────────────────────────────────── */}
        <TabsContent value="campaigns">
          {campaigns.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted mb-4">
                <Send className="h-8 w-8 text-muted-foreground/60" />
              </div>
              <h3 className="font-semibold mb-1">No campaigns yet</h3>
              <p className="text-sm text-muted-foreground mb-4 max-w-xs">
                Create your first campaign to start reaching out to your customers.
              </p>
              <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" /> Create Campaign
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {campaigns.map((campaign) => (
                <Card key={campaign.id} className="flex flex-col overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-card">
                  {/* Status accent bar */}
                  <div className={cn("h-1 w-full shrink-0", STATUS_LEFT[campaign.status] ?? 'bg-border')} />

                  <CardHeader className="pb-2 pt-4">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-sm font-semibold leading-snug line-clamp-1">
                          {campaign.title}
                        </CardTitle>
                        <CardDescription className="text-xs mt-0.5 line-clamp-1">
                          {campaign.description || (campaign.campaign_type === 'one_off' ? 'One-time campaign' : 'Ongoing campaign')}
                        </CardDescription>
                      </div>
                      <Badge variant="outline" className={cn("shrink-0 text-[10px] h-5 px-1.5 gap-1", STATUS_BADGE[campaign.status] ?? '')}>
                        <StatusIcon status={campaign.status} />
                        <span className="capitalize">{campaign.status}</span>
                      </Badge>
                    </div>
                  </CardHeader>

                  <CardContent className="flex-1 space-y-3 pt-0 pb-3">
                    {/* Channels + type */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1 flex-wrap">
                        {campaign.enabled_channels.map((ch) => (
                          <span key={ch} className="inline-flex items-center gap-1 text-[10px] text-muted-foreground bg-muted rounded px-1.5 py-0.5 capitalize">
                            <ChannelIcon channel={ch} size="h-3 w-3" />
                            {ch}
                          </span>
                        ))}
                      </div>
                      <Badge variant="secondary" className="text-[10px] h-5 shrink-0">
                        {campaign.campaign_type === 'one_off' ? 'One-time' : 'Ongoing'}
                      </Badge>
                    </div>

                    {/* Stats grid */}
                    <div className="grid grid-cols-3 gap-1.5">
                      {[
                        { label: 'Recipients', value: campaign.total_recipients.toLocaleString() },
                        { label: 'Delivered',  value: campaign.delivered_count.toLocaleString() },
                        { label: 'Engagement', value: `${calcEngagement(campaign)}%` },
                      ].map(({ label, value }) => (
                        <div key={label} className="rounded-lg bg-muted/50 px-2 py-1.5 text-center">
                          <p className="text-xs font-semibold tabular-nums">{value}</p>
                          <p className="text-[10px] text-muted-foreground leading-tight">{label}</p>
                        </div>
                      ))}
                    </div>

                    {/* Progress */}
                    {campaign.delivered_count > 0 && (
                      <div className="space-y-1">
                        <div className="flex justify-between text-[10px] text-muted-foreground">
                          <span>Engagement rate</span>
                          <span>{calcEngagement(campaign)}%</span>
                        </div>
                        <Progress value={calcEngagement(campaign)} className="h-1" />
                      </div>
                    )}

                    {/* Scheduled time */}
                    {campaign.scheduled_at && (
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3 shrink-0" />
                        Scheduled: {formatDate(campaign.scheduled_at)}
                      </p>
                    )}
                  </CardContent>

                  <Separator />

                  <CardFooter className="flex items-center gap-1.5 pt-2.5 pb-2.5 px-4">
                    {campaign.status === 'draft' && (
                      <Button size="sm" className="h-7 text-xs gap-1"
                        onClick={() => handleCampaignAction(campaign.id, 'start')}>
                        <Play className="h-3 w-3" /> Start
                      </Button>
                    )}
                    {campaign.status === 'active' && (
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                        onClick={() => handleCampaignAction(campaign.id, 'pause')}>
                        <Pause className="h-3 w-3" /> Pause
                      </Button>
                    )}
                    {campaign.status === 'paused' && (
                      <Button size="sm" className="h-7 text-xs gap-1"
                        onClick={() => handleCampaignAction(campaign.id, 'resume')}>
                        <Play className="h-3 w-3" /> Resume
                      </Button>
                    )}
                    <p className="text-[10px] text-muted-foreground ml-1">
                      {formatDate(campaign.created_at)}
                    </p>
                    <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs gap-1 text-muted-foreground">
                      <BarChart2 className="h-3 w-3" /> Stats
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Templates Tab ──────────────────────────────────────────────────── */}
        <TabsContent value="templates">
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Campaign Templates</CardTitle>
                  <CardDescription className="text-xs mt-0.5">
                    Reusable message templates for your campaigns
                  </CardDescription>
                </div>
                <Button size="sm" onClick={() => setTemplateDialogOpen(true)} className="gap-1.5">
                  <Plus className="h-3.5 w-3.5" /> New Template
                </Button>
              </div>
            </CardHeader>
            <Separator />
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs">Name</TableHead>
                  <TableHead className="text-xs">Category</TableHead>
                  <TableHead className="text-xs">Type</TableHead>
                  <TableHead className="text-xs">Usage</TableHead>
                  <TableHead className="text-xs">Created</TableHead>
                  <TableHead className="text-xs text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-16">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <FileText className="h-8 w-8 opacity-40" />
                        <p className="text-sm">No templates yet</p>
                        <Button size="sm" variant="outline" onClick={() => setTemplateDialogOpen(true)} className="mt-1 gap-1">
                          <Plus className="h-3.5 w-3.5" /> Create Template
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  templates.map((tpl) => (
                    <TableRow key={tpl.id}>
                      <TableCell>
                        <div className="font-medium text-sm">{tpl.name}</div>
                        {tpl.description && (
                          <div className="text-xs text-muted-foreground line-clamp-1">{tpl.description}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="capitalize text-xs">{tpl.category}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground capitalize">{tpl.message_type}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Users className="h-3 w-3" />
                          {tpl.usage_count}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDate(tpl.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm" variant="outline"
                          className="h-7 text-xs"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              message_content: tpl.message_content,
                              message_type: tpl.message_type,
                            }));
                            setCreateDialogOpen(true);
                          }}
                        >
                          Use Template
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* ── WhatsApp Templates Tab ─────────────────────────────────────────── */}
        <TabsContent value="whatsapp">
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">WhatsApp Templates</CardTitle>
                  <CardDescription className="text-xs mt-0.5">
                    Approved templates for WhatsApp Business messaging
                  </CardDescription>
                </div>
                <Button size="sm" variant="outline" onClick={fetchData} className="gap-1.5">
                  <RefreshCw className="h-3.5 w-3.5" /> Sync
                </Button>
              </div>
            </CardHeader>
            <Separator />
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs">Template Name</TableHead>
                  <TableHead className="text-xs">Category</TableHead>
                  <TableHead className="text-xs">Language</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Quality</TableHead>
                  <TableHead className="text-xs text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {whatsappTemplates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-16">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <MessageCircle className="h-8 w-8 opacity-40" />
                        <p className="text-sm">No WhatsApp templates found</p>
                        <p className="text-xs max-w-xs">Sync your templates from the WhatsApp Business API to get started.</p>
                        <Button size="sm" variant="outline" onClick={fetchData} className="mt-1 gap-1">
                          <RefreshCw className="h-3.5 w-3.5" /> Sync Templates
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  whatsappTemplates.map((tpl) => (
                    <TableRow key={tpl.id}>
                      <TableCell>
                        <div className="font-medium text-sm">{tpl.name}</div>
                        {tpl.variables.length > 0 && (
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {tpl.variables.length} variable{tpl.variables.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs capitalize">{tpl.category.toLowerCase()}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground uppercase">{tpl.language_code}</TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={cn("text-[10px] capitalize", WA_STATUS_BADGE[tpl.approval_status.toLowerCase()] ?? '')}
                        >
                          {tpl.approval_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {tpl.quality_score ? (
                          <span className={cn(
                            "text-xs font-medium",
                            tpl.quality_score === 'HIGH' ? 'text-emerald-600'
                            : tpl.quality_score === 'MEDIUM' ? 'text-amber-600'
                            : 'text-red-600'
                          )}>
                            {tpl.quality_score}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm" variant="outline"
                          className="h-7 text-xs"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              whatsapp_template_id: tpl.id,
                              enabled_channels: prev.enabled_channels.includes('whatsapp')
                                ? prev.enabled_channels
                                : [...prev.enabled_channels, 'whatsapp'],
                            }));
                            setCreateDialogOpen(true);
                          }}
                        >
                          Use in Campaign
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Create Campaign Dialog ──────────────────────────────────────────── */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
          <DialogHeader className="shrink-0 space-y-1.5 border-b border-border px-6 py-3.5 pr-14 text-left">
            <DialogTitle>Create New Campaign</DialogTitle>
            <DialogDescription>
              Configure channels, scheduling, and message content. You can attach an approved WhatsApp template or send a custom message.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <div className="space-y-6 px-6 py-5">
            {/* Basic Info */}
            <div className="flex flex-col gap-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Basic Info</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="c-title">Campaign Title</Label>
                  <Input
                    id="c-title"
                    value={formData.title}
                    onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
                    placeholder="e.g. Summer Promo 2026"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="c-type">Campaign Type</Label>
                  <select id="c-type" value={formData.campaign_type}
                    onChange={(e) => setFormData((p) => ({ ...p, campaign_type: e.target.value as any }))}
                    className={SELECT_CLS}>
                    <option value="one_off">One-time</option>
                    <option value="ongoing">Ongoing</option>
                  </select>
                </div>
                <div className="sm:col-span-2 flex flex-col gap-1.5">
                  <Label htmlFor="c-desc">Description</Label>
                  <textarea id="c-desc" value={formData.description} rows={2}
                    onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm resize-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
                </div>
              </div>
            </div>

            <Separator />

            {/* Scheduling */}
            <div className="flex flex-col gap-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Scheduling</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="c-trigger">Trigger Type</Label>
                  <select id="c-trigger" value={formData.trigger_type}
                    onChange={(e) => setFormData((p) => ({ ...p, trigger_type: e.target.value as any }))}
                    className={SELECT_CLS}>
                    <option value="immediate">Send Immediately</option>
                    <option value="scheduled">Schedule for Later</option>
                    <option value="event_based">Event-based</option>
                  </select>
                </div>
                {formData.trigger_type === 'scheduled' && (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="c-scheduled">Scheduled Time</Label>
                    <input id="c-scheduled" type="datetime-local" value={formData.scheduled_at}
                      onChange={(e) => setFormData((p) => ({ ...p, scheduled_at: e.target.value }))}
                      className={SELECT_CLS} />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.send_only_business_hours}
                  onCheckedChange={(v) => setFormData((p) => ({ ...p, send_only_business_hours: v }))}
                />
                <Label className="text-sm">Send only during business hours</Label>
              </div>
            </div>

            <Separator />

            {/* Message */}
            <div className="flex flex-col gap-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Message</p>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="c-message">Message Content</Label>
                <textarea id="c-message" value={formData.message_content} rows={4}
                  onChange={(e) => setFormData((p) => ({ ...p, message_content: e.target.value }))}
                  placeholder="Use {{first_name}}, {{email}}, etc. for personalization"
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm resize-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
                <p className="text-xs text-muted-foreground">
                  Use {'{{first_name}}'}, {'{{email}}'} for personalization
                </p>
              </div>
            </div>

            <Separator />

            {/* WhatsApp Settings */}
            <div className="flex flex-col gap-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">WhatsApp Template Settings</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="c-wa-template">WhatsApp Template</Label>
                  <select id="c-wa-template" value={formData.whatsapp_template_id ?? ''}
                    onChange={(e) => {
                      const val = e.target.value ? Number(e.target.value) : null;
                      setFormData((p) => {
                        const updated = { ...p, whatsapp_template_id: val };
                        if (val && !p.enabled_channels.includes('whatsapp')) {
                          updated.enabled_channels = [...p.enabled_channels, 'whatsapp'];
                        }
                        return updated;
                      });
                    }}
                    className={SELECT_CLS}>
                    <option value="">None (Use custom message)</option>
                    {whatsappTemplates.map((tpl) => (
                      <option key={tpl.id} value={tpl.id}>{tpl.name} ({tpl.category})</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="c-tpl-cat">Template Category Filter</Label>
                  <select id="c-tpl-cat" value={formData.template_category}
                    onChange={(e) => setFormData((p) => ({ ...p, template_category: e.target.value }))}
                    className={SELECT_CLS}>
                    <option value="">All Categories</option>
                    <option value="MARKETING">Marketing</option>
                    <option value="UTILITY">Utility</option>
                    <option value="AUTHENTICATION">Authentication</option>
                  </select>
                </div>

                {formData.whatsapp_template_id && (
                  <>
                    <div className="sm:col-span-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300">
                      WhatsApp template selected. Configure rate limiting and session settings below.
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="c-rate">Rate Limit (per minute)</Label>
                      <Input id="c-rate" type="number" value={formData.rate_limit_per_minute}
                        onChange={(e) => setFormData((p) => ({ ...p, rate_limit_per_minute: parseInt(e.target.value) || 100 }))} />
                      <p className="text-xs text-muted-foreground">Max messages per minute</p>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="c-retries">Max Retries</Label>
                      <Input id="c-retries" type="number" value={formData.max_retries}
                        onChange={(e) => setFormData((p) => ({ ...p, max_retries: parseInt(e.target.value) || 3 }))} />
                      <p className="text-xs text-muted-foreground">Retry failed sends</p>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="c-cooldown">Cooldown (hours)</Label>
                      <Input id="c-cooldown" type="number" value={formData.template_cooldown_hours}
                        onChange={(e) => setFormData((p) => ({ ...p, template_cooldown_hours: parseInt(e.target.value) || 48 }))} />
                      <p className="text-xs text-muted-foreground">Min hours between template sends</p>
                    </div>
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center gap-2">
                        <Switch checked={formData.enforce_24h_window}
                          onCheckedChange={(v) => setFormData((p) => ({ ...p, enforce_24h_window: v }))} />
                        <Label className="text-sm">Enforce 24-hour session window</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={formData.retry_failed}
                          onCheckedChange={(v) => setFormData((p) => ({ ...p, retry_failed: v }))} />
                        <Label className="text-sm">Retry failed messages</Label>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 gap-2.5 border-t border-border px-6 py-3.5">
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCampaign} disabled={!formData.title}>
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Create Template Dialog ─────────────────────────────────────────── */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
          <DialogHeader className="shrink-0 space-y-1.5 border-b border-border px-6 py-3.5 pr-14 text-left">
            <DialogTitle>Create Campaign Template</DialogTitle>
            <DialogDescription>
              Reusable copy for outbound campaigns. Variables use the same {'{{name}}'} syntax as campaigns.
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <div className="space-y-5 px-6 py-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="t-name">Template Name</Label>
                <Input id="t-name" value={templateForm.name}
                  onChange={(e) => setTemplateForm((p) => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="t-cat">Category</Label>
                <select id="t-cat" value={templateForm.category}
                  onChange={(e) => setTemplateForm((p) => ({ ...p, category: e.target.value }))}
                  className={SELECT_CLS}>
                  <option value="general">General</option>
                  <option value="welcome">Welcome</option>
                  <option value="promotional">Promotional</option>
                  <option value="support">Support</option>
                  <option value="re_engagement">Re-engagement</option>
                </select>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="t-desc">Description</Label>
              <textarea id="t-desc" value={templateForm.description} rows={2}
                onChange={(e) => setTemplateForm((p) => ({ ...p, description: e.target.value }))}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm resize-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="t-content">Message Content</Label>
              <textarea id="t-content" value={templateForm.message_content} rows={4}
                onChange={(e) => setTemplateForm((p) => ({ ...p, message_content: e.target.value }))}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm resize-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
            </div>
            </div>
          </div>
          <DialogFooter className="shrink-0 gap-2.5 border-t border-border px-6 py-3.5">
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateTemplate} disabled={!templateForm.name}>
              Create Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default CampaignsPage;
